import { INumberedDocumentView, IPhysicalBuilding } from '../../api-client/autogenerated';

export type PunchListExportTreeItem = { id: string; label: string };

export type PunchListExportTreeLocation = {
  id: string;
  label: string;
  items: PunchListExportTreeItem[];
};

const NO_LOCATION_ID = 'no-location';
const MAX_ITEM_LABEL_LENGTH = 80;

const itemLabel = (document: INumberedDocumentView) => {
  const description = document.description?.trim() || 'No description';
  const shortened =
    description.length > MAX_ITEM_LABEL_LENGTH
      ? `${description.slice(0, MAX_ITEM_LABEL_LENGTH - 1)}…`
      : description;
  return document.isDraft ? `${shortened} (Draft)` : shortened;
};

/** Alphanumeric, so "Room 2" comes before "Room 10". */
const compareNames = (a: string, b: string) => a.localeCompare(b, 'en', { numeric: true });

/**
 * The punch list as the export dialog offers it: each location, named by its building and floor,
 * with its items underneath. Locations sort by building, then floor, then name, with items that
 * have none last; items sort by label within them. Deleted items are left out -- the export would
 * drop them anyway.
 */
export const buildPunchListExportTree = (
  documents: INumberedDocumentView[],
  buildings: IPhysicalBuilding[],
): PunchListExportTreeLocation[] => {
  const locationLabels = new Map<string, string>();
  const locations: { id: string; path: string[] }[] = [];
  buildings.forEach((building) =>
    (building.floors ?? []).forEach((floor) =>
      (floor.locations ?? []).forEach((location) => {
        const path = [building.name, floor.name, location.name];
        locationLabels.set(location.id, path.join(' / '));
        locations.push({ id: location.id, path });
      }),
    ),
  );
  const locationOrder = locations
    .sort((a, b) => {
      for (let i = 0; i < a.path.length; i += 1) {
        const order = compareNames(a.path[i], b.path[i]);
        if (order !== 0) return order;
      }
      return 0;
    })
    .map((location) => location.id);

  const itemsByLocation = new Map<string, PunchListExportTreeItem[]>();
  documents
    .filter((document) => !document.isHidden)
    .forEach((document) => {
      const locationId =
        document.physicalLocationId && locationLabels.has(document.physicalLocationId)
          ? document.physicalLocationId
          : NO_LOCATION_ID;
      const items = itemsByLocation.get(locationId) ?? [];
      items.push({ id: document.id, label: itemLabel(document) });
      itemsByLocation.set(locationId, items);
    });

  itemsByLocation.forEach((items) => items.sort((a, b) => compareNames(a.label, b.label)));

  const tree = locationOrder
    .filter((locationId) => itemsByLocation.has(locationId))
    .map((locationId) => ({
      id: locationId,
      label: locationLabels.get(locationId)!,
      items: itemsByLocation.get(locationId)!,
    }));

  const unplaced = itemsByLocation.get(NO_LOCATION_ID);
  if (unplaced) tree.push({ id: NO_LOCATION_ID, label: 'No location', items: unplaced });
  return tree;
};

export const allItemIds = (tree: PunchListExportTreeLocation[]) =>
  tree.flatMap((location) => location.items.map((item) => item.id));
